import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { getSyncStatusMeta } from '../../utils/productSelectionsSync';

const SyncSelectionsDialog = ({
  open,
  onClose,
  isMobile,
  syncCounts,
  syncQueueLength,
  syncSending,
  syncProgress,
  syncRows,
  onClearSelections,
  onSelectFailedRows,
}) => {
  const hasFailedSyncRows = syncCounts.failed > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      data-testid="sync-di-modal"
      PaperProps={{
        sx: {
          height: { xs: '100dvh', sm: 'auto' },
          maxHeight: { xs: '100dvh', sm: 'calc(100% - 64px)' },
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">Sync selections to DI</Typography>
            <Typography variant="body2" color="text.secondary">
              Status and date values will be synced from DI and sent to VMT.
            </Typography>
          </Box>
          {isMobile ? (
            <IconButton aria-label="Close sync modal" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        data-testid="sync-di-modal-content"
        sx={{
          gridRow: 2,
          minHeight: 0,
          overflow: 'auto',
          position: 'relative',
          zIndex: 0,
          contain: 'paint',
          pb: { xs: 10, sm: 2 },
        }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Chip size="small" variant="outlined" label={`Total: ${syncCounts.total}`} />
            <Chip size="small" variant="outlined" label={`Queued: ${syncCounts.queued}`} />
            <Chip size="small" variant="outlined" label={`Sending: ${syncCounts.sending}`} />
            <Chip size="small" variant="outlined" color="success" label={`Success: ${syncCounts.success}`} />
            <Chip size="small" variant="outlined" color="error" label={`Failed: ${syncCounts.failed}`} />
            <Chip size="small" variant="outlined" color="warning" label={`Skipped: ${syncCounts.skipped}`} />
            {syncQueueLength > 0 || syncSending ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Processing {syncProgress.processed} / {syncProgress.total} batches
                </Typography>
              </Stack>
            ) : null}
          </Stack>
          {syncProgress.total > 0 ? (
            <LinearProgress
              variant="determinate"
              value={Math.min((syncProgress.processed / syncProgress.total) * 100, 100)}
            />
          ) : null}
          <Divider />
          {syncRows.length === 0 ? (
            <Alert severity="info">No selections queued for sync.</Alert>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              data-testid="sync-di-modal-table-container"
              sx={{
                maxHeight: isMobile ? '45dvh' : '60vh',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>BAC</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Reference ID</TableCell>
                    <TableCell>DI Status</TableCell>
                    <TableCell>DI Selection Date</TableCell>
                    <TableCell>DI Effective Date</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncRows.map((item) => {
                    const meta = getSyncStatusMeta(item.status);
                    const reason =
                      item.reason ||
                      (item.status === 'success'
                        ? 'Sent.'
                        : item.status === 'sending'
                          ? 'Sending...'
                          : item.status === 'queued'
                            ? 'Queued.'
                            : '');
                    return (
                      <TableRow key={item.id} data-testid={`sync-row-${item.id}`}>
                        <TableCell>{item.row?.bac || ''}</TableCell>
                        <TableCell>{item.row?.programName || ''}</TableCell>
                        <TableCell>{item.row?.productBrandCode || item.row?.productBrandCd || ''}</TableCell>
                        <TableCell>{item.referenceId || ''}</TableCell>
                        <TableCell>{item.diStatus || ''}</TableCell>
                        <TableCell>{item.diSelectionDate || ''}</TableCell>
                        <TableCell>{item.diEffectiveDate || ''}</TableCell>
                        <TableCell>
                          <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          gridRow: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={() => {
            onClose();
            onClearSelections();
          }}
        >
          Clear selections
        </Button>
        {hasFailedSyncRows ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const failedIds = syncRows.filter((item) => item.status === 'failed').map((item) => item.id);
              onClose();
              onSelectFailedRows(failedIds);
            }}
          >
            Select failed rows
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default SyncSelectionsDialog;
